import { Col, Row, SelectPicker, Tabs, Text } from 'rsuite'
import useModelAlias from '@/hooks/useModelAlias'
import useTranslation from '@/hooks/useTranslation'
import { useCallback, useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { showWarning } from '@/components/shared/toaster'
import { mapValues, groupBy, uniqBy, orderBy } from 'lodash'
import React from 'react'
import cn from 'classnames'
import styles from './vehicleSelector.module.scss'
import CustomGrid from '@/components/shared/customGrid'
import { Divider } from 'rsuite'
import { useIsMobile } from '@/hooks/useIsMobile'
import ExternalWebsiteRedirectModal from './externalWebsiteRedirectModal'
import { configSelectors } from '@/store/configs'
import { customerHelper } from '@tweddle-se-team/common/constants'
import VehicleSelectorTextModel from './vehicleSelectorTextModel'
import VehicleSelectorImageModel from '@/components/themes/subaru-nordics/landing/vehicleSelectorImageModel'
import getNextPageInFlowSubaru from './vehicleSelectorFlow'

const { CUSTOMER_IDS } = customerHelper

const showVehicleNotFoundByCustomer = {
  [CUSTOMER_IDS.SUBARU]: () => true,
  [CUSTOMER_IDS.NORDICS]: () => false
}

const showModelSearchByCustomer = {
  [CUSTOMER_IDS.SUBARU]: () => true,
  [CUSTOMER_IDS.NORDICS]: () => false
}

const showStandaloneTitleByCustomer = {
  [CUSTOMER_IDS.SUBARU]: () => false,
  [CUSTOMER_IDS.NORDICS]: () => true
}

const vehicleSelectorModelByCustomer = {
  [CUSTOMER_IDS.SUBARU]: VehicleSelectorTextModel,
  [CUSTOMER_IDS.NORDICS]: VehicleSelectorImageModel
}

const VehicleNotFound = ({ isMobile, t, setShowRedirectModal }) => {
  const notFoundMessage = t('vehicle_not_found', "Can't find your vehicle? Try this link")
  return (
    <div
      className={cn(styles.vehicleSelectorNotFound, { [styles.vehicleSelectorNotFoundMobile]: isMobile })}
      role="button"
      tabIndex={0}
      onClick={() => setShowRedirectModal(true)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setShowRedirectModal(true)
        }
      }}
    >
      {notFoundMessage}
    </div>
  )
}

const getGroupedByYearAndModel = products =>
  mapValues(groupBy(products, 'year'), yearGroup => {
    const modelsGrouped = groupBy(yearGroup, 'model')
    const sortedModelKeys = orderBy(Object.keys(modelsGrouped), [], ['asc'])
    const sortedModels = {}
    sortedModelKeys.forEach(model => {
      sortedModels[model] = uniqBy(modelsGrouped[model], item => JSON.stringify(item))
    })
    return sortedModels
  })

const VehicleSelector = ({ i18nL10n, products, modelLangMarketingNames, productImages }) => {
  const router = useRouter()
  const t = useTranslation()
  const { locale } = i18nL10n
  const getModelAlias = useModelAlias(modelLangMarketingNames, locale)
  const [search, setSearch] = useState('')
  const [showRedirectModal, setShowRedirectModal] = useState(false)
  const isMobile = useIsMobile()
  const customerId = useSelector(configSelectors.selectCustomerId)
  const showVehicleNotFound = (
    showVehicleNotFoundByCustomer[customerId] || showVehicleNotFoundByCustomer[CUSTOMER_IDS.SUBARU]
  )()
  const showModelSearch = (showModelSearchByCustomer[customerId] || showModelSearchByCustomer[CUSTOMER_IDS.SUBARU])()
  const showStandaloneTitle = (
    showStandaloneTitleByCustomer[customerId] || showStandaloneTitleByCustomer[CUSTOMER_IDS.SUBARU]
  )()
  // Group by year and model, sort model names alphabetically - Memoized for performance
  const filteredProducts = useMemo(
    () =>
      search
        ? products.all.filter(product => product.model.toLowerCase().includes(search.toLowerCase()))
        : products.all,
    [search, products.all]
  )

  const allProductsByYearAndModel = useMemo(() => getGroupedByYearAndModel(products.all), [products.all])

  const groupedByYearAndModel = useMemo(() => getGroupedByYearAndModel(filteredProducts), [filteredProducts])

  // Sort years alphabetically for filtered products and all products
  const allSortedYears = useMemo(
    () => orderBy(Object.keys(allProductsByYearAndModel), [], ['desc']),
    [allProductsByYearAndModel]
  )

  const sortedYears = useMemo(() => orderBy(Object.keys(groupedByYearAndModel), [], ['desc']), [groupedByYearAndModel])

  // State for active year and search
  const [activeYear, setActiveYear] = useState(sortedYears[0])

  // Gather all unique models for all years for the SelectPicker (not only for filtered products)
  const allModels = useMemo(
    () => Array.from(new Set(allSortedYears.flatMap(year => Object.keys(allProductsByYearAndModel[year])))),
    [allSortedYears, allProductsByYearAndModel]
  )

  const modelOptions = useMemo(
    () => allModels.map(model => ({ label: getModelAlias(model, 'model'), value: model })),
    [allModels, getModelAlias]
  )

  const selectProduct = useCallback(
    (modelData, locale) => {
      const { page, query, error } = getNextPageInFlowSubaru(modelData, locale)
      if (error) {
        showWarning(t(error))
      }
      router.push(`/${page}?${new URLSearchParams(query).toString()}`)
    },
    [router, t]
  )

  // Helper function to generate model panels for a given year
  const getModelPanelsForYear = useCallback(
    year => {
      const models = orderBy(Object.keys(groupedByYearAndModel[year] || {}), [], ['asc'])
      return models.map(model => {
        const modelData = groupedByYearAndModel[year]?.[model] || []
        const onClickModel = () => selectProduct(modelData, locale)
        const modelAlias = getModelAlias(model, 'model')
        const VehicleSelectorModel =
          vehicleSelectorModelByCustomer[customerId] || vehicleSelectorModelByCustomer[CUSTOMER_IDS.SUBARU]

        return (
          <VehicleSelectorModel
            key={model}
            model={model}
            modelAlias={modelAlias}
            year={year}
            modelData={modelData}
            onClickModel={onClickModel}
            productImages={productImages}
          />
        )
      })
    },
    [groupedByYearAndModel, selectProduct, locale, getModelAlias, customerId, productImages]
  )

  const CustomCaret = useCallback(
    () => (
      <span className={styles.vehicleSelectorCaret}>
        <svg width="24" height="24" viewBox="0 0 24 24" style={{ display: 'block' }}>
          <polyline
            points="7 10 12 15 17 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    ),
    []
  )

  const handleSearchChange = useCallback(
    value => {
      setSearch(value || '')
      if (value) {
        let foundYear = null
        // Find and set the first year available for the selected model
        for (const year of allSortedYears) {
          const models = Object.keys(allProductsByYearAndModel[year])
          const match = models.find(m => m === value)
          if (match) {
            foundYear = year
            break
          }
        }
        if (foundYear) {
          setActiveYear(foundYear)
        }
      }
    },
    [allSortedYears, allProductsByYearAndModel]
  )

  const handleYearSelect = useCallback(year => {
    setActiveYear(year)
  }, [])

  const getInfoMessage = () => {
    return (
      <p className={styles.modelsInfoMessage}>
        {t(
          'landing_we_are_growing',
          "We are growing! We appreciate your patience as we add new models for the 2026 model year to improve your Subaru owner's information experience. For models not shown here please click the link above."
        )}
      </p>
    )
  }

  return (
    <>
      <ExternalWebsiteRedirectModal
        show={showRedirectModal}
        close={() => setShowRedirectModal(false)}
        url="https://www.subaru.com/owners"
      />
      <Row>
        <Col xs={24} md={12}>
          <Divider className={styles.vehicleSelectorDivider}>
            <b>{t('or', 'OR')}</b>
          </Divider>
        </Col>
      </Row>
      {showStandaloneTitle && (
        <Row>
          <Col xs={24}>
            <Text className="mr-20 choose-vehicle" as="h3">
              {t('homepage_choose_vehicle', 'Choose your vehicle')}
            </Text>
          </Col>
        </Row>
      )}
      {showModelSearch && (
        <Row>
          <Col xs={24} md={12}>
            <Text className="mb-10 mr-20 choose-vehicle" as="h2">
              {t('homepage_choose_vehicle')}
            </Text>
            <div className={styles.vehicleSelectorSearch}>
              <SelectPicker
                data={modelOptions}
                value={search || null}
                caretAs={CustomCaret}
                onChange={handleSearchChange}
                placeholder={t('homepage_model_select_placeholder')}
                searchable
                cleanable
                className={styles.vehicleSelectorPicker}
                style={{ paddingRight: 32 }}
              />
            </div>
          </Col>
        </Row>
      )}
      {showModelSearch && search && !filteredProducts.length && (
        <Row>
          <Col>
            <div className={styles.vehicleSelectorNoMatch}>{t('No matching model found')}</div>
          </Col>
        </Row>
      )}
      {/** Filtered products is ALL products if no search criteria is applied */}
      {filteredProducts.length > 0 && (
        <Row className={styles.vehicleSelectorYearButtonWrapper}>
          <Col xs={24}>
            {!isMobile && showVehicleNotFound && (
              <VehicleNotFound isMobile={isMobile} t={t} setShowRedirectModal={setShowRedirectModal} />
            )}
            <Tabs
              className={styles.vehicleSelectorYears}
              activeKey={activeYear}
              appearance="subtle"
              aria-label="Select model year"
              onSelect={handleYearSelect}
            >
              {sortedYears.map(year => (
                <Tabs.Tab key={year} eventKey={year} title={year}>
                  {!isMobile && getInfoMessage()}
                  <CustomGrid
                    items={getModelPanelsForYear(year)}
                    preComponent={
                      isMobile && showVehicleNotFound ? (
                        <>
                          <VehicleNotFound isMobile={isMobile} t={t} setShowRedirectModal={setShowRedirectModal} />
                        </>
                      ) : null
                    }
                    postComponent={isMobile ? getInfoMessage() : null}
                  />
                </Tabs.Tab>
              ))}
            </Tabs>
          </Col>
        </Row>
      )}
    </>
  )
}

export default VehicleSelector
